/**
 * VideoTab: 설정 다이얼로그 > 비디오 탭
 * v1 원본: RenewalComponents/Footer/SettingsDialog/VideoTab
 * Redux connect → useAppSelector/useAppDispatch 전환
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAppSelector, useAppDispatch } from '@/store/hooks';
import { localActions } from '@/store/slices/localSlice';

interface VideoTabProps {
  onChangeDevice?: (type: string, deviceId: string) => void;
}

const VideoTab: React.FC<VideoTabProps> = ({ onChangeDevice }) => {
  const dispatch = useAppDispatch();
  const local = useAppSelector((s) => s.local);
  const videoInputDeviceId = local.videoInputDeviceId;
  const isVideoReverse = local.isVideoReverse;

  const [cameraDevices, setCameraDevices] = useState<MediaDeviceInfo[]>([]);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);

  // 카메라 목록 가져오기
  const loadCameraDevices = useCallback(async () => {
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const cameras = devices.filter((d) => d.kind === 'videoinput');
      setCameraDevices(cameras);
    } catch (err) {
      console.warn('[VideoTab] enumerateDevices failed:', err);
    }
  }, []);

  useEffect(() => {
    loadCameraDevices();
    navigator.mediaDevices.addEventListener('devicechange', loadCameraDevices);
    return () => {
      navigator.mediaDevices.removeEventListener('devicechange', loadCameraDevices);
    };
  }, [loadCameraDevices]);

  // 드롭다운 외부 클릭 닫기
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsDropdownOpen(false);
      }
    };
    if (isDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isDropdownOpen]);

  // 카메라 미리보기
  useEffect(() => {
    const startCamera = async () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
        streamRef.current = null;
      }

      if (!videoInputDeviceId) return;

      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: {
            deviceId: { exact: videoInputDeviceId },
            width: 1280,
            height: 720,
          },
        });

        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          videoRef.current.onloadedmetadata = () => videoRef.current?.play();
        }
      } catch (err) {
        console.warn('[VideoTab] getUserMedia failed:', err);
      }
    };

    startCamera();

    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
        streamRef.current = null;
      }
    };
  }, [videoInputDeviceId]);

  const handleSelectCamera = useCallback(
    (device: MediaDeviceInfo) => {
      if (onChangeDevice) {
        onChangeDevice('videoinput', device.deviceId);
      } else {
        dispatch(localActions.setVideoInputDeviceId(device.deviceId));
      }
      setIsDropdownOpen(false);
    },
    [dispatch, onChangeDevice]
  );

  const handleToggleMirror = useCallback(() => {
    dispatch(localActions.toggleVideoReverse());
  }, [dispatch]);

  const selectedLabel =
    cameraDevices.find((d) => d.deviceId === videoInputDeviceId)?.label || '카메라를 선택하세요';

  const hasCam = !!videoInputDeviceId;

  return (
    <>
      {/* 카메라 미리보기 영역 */}
      <div
        className="rtc14_screen rtc14_screen--inter4"
        style={{
          position: 'relative',
          overflow: 'hidden',
          background: '#1a1a1a',
        }}
      >
        {hasCam ? (
          <video
            ref={videoRef}
            autoPlay
            muted
            playsInline
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              transform: isVideoReverse ? '' : 'rotateY(180deg)',
              display: 'block',
            }}
          />
        ) : (
          <div
            style={{
              width: '100%',
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexDirection: 'column',
              gap: 8,
              color: '#888',
              fontSize: 13,
            }}
          >
            <span className="lunacon lunacon--videoOff-outlined" style={{ fontSize: 32 }} />
            <span>카메라가 없거나 선택되지 않았습니다.</span>
          </div>
        )}
      </div>

      {/* 설정 목록 */}
      <dl className="rtcDialogSetupNew__info v2">
        {/* 카메라 선택 */}
        <div>
          <dt>카메라</dt>
          <dd>
            <div className={`rtc_newDropdown${isDropdownOpen ? ' open' : ''}`} ref={dropdownRef}>
              <button
                type="button"
                className="rtc_newDropdown__btn"
                onClick={() => setIsDropdownOpen((prev) => !prev)}
              >
                <span>{selectedLabel}</span>
                <span className="lunacon lunacon--arrowDown-outlined" />
              </button>
              {isDropdownOpen && (
                <ul className="rtc_newDropdown_list">
                  {cameraDevices.length === 0 ? (
                    <li>
                      <button type="button" className="rtc_newDropdown_listBtn" disabled>
                        사용 가능한 카메라 없음
                      </button>
                    </li>
                  ) : (
                    cameraDevices.map((device) => {
                      const isSelected = videoInputDeviceId === device.deviceId;
                      return (
                        <li key={device.deviceId}>
                          <button
                            type="button"
                            className={'rtc_newDropdown_listBtn' + (isSelected ? ' selected' : '')}
                            onClick={() => handleSelectCamera(device)}
                          >
                            {device.label || `Camera ${device.deviceId.slice(0, 8)}`}
                          </button>
                        </li>
                      );
                    })
                  )}
                </ul>
              )}
            </div>
          </dd>
        </div>

        {/* 거울 모드 */}
        <div>
          <dt>거울 모드(좌우반전)</dt>
          <dd>
            <div className="common_onoff_switch small2 type_blue2">
              <label>
                <input
                  className="onoff_input"
                  type="checkbox"
                  checked={isVideoReverse}
                  onChange={handleToggleMirror}
                />
                <span className="switch_img" />
                <span className="label_text on" />
                <span className="label_text off" />
              </label>
            </div>
          </dd>
        </div>
      </dl>
    </>
  );
};

export default VideoTab;
